import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../services/supabase';
import { useLovense } from '../../hooks/useLovense';
import { useSessionStore } from '../../hooks/useSession';
import { SafeWordButton } from '../SafeWordButton';
import toast from 'react-hot-toast';

export const RhythmGame = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'A') as 'A' | 'B';
  
  const { vibrate, stop } = useLovense();
  const { session } = useSessionStore();
  
  const [score, setScore] = useState(0);
  const [partnerScore, setPartnerScore] = useState(0);
  const [beatActive, setBeatActive] = useState(false);
  const [combo, setCombo] = useState(0);
  const [gameActive, setGameActive] = useState(true);
  const [winner, setWinner] = useState<'A' | 'B' | 'tie' | null>(null);
  
  const bpm = 60;
  const beatInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastBeatTime = useRef(0);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const channel = supabase.channel(`game-${sessionId}`);
    channelRef.current = channel;
    
    channel
      .on('broadcast', { event: 'game_action' }, ({ payload }: any) => {
        const { action, response } = payload;
        if (action.playerId !== role) {
          if (action.action === 'beat_hit') {
            setPartnerScore(response.score);
          }
        }
      })
      .on('broadcast', { event: 'game_ended' }, ({ payload }: any) => {
        const { winner: gameWinner } = payload;
        setWinner(gameWinner);
        setGameActive(false);
        stop();
        
        if (gameWinner === role) {
          toast.success('You won! 🎉');
          vibrate(20);
          setTimeout(() => vibrate(0), 2000);
        } else if (gameWinner === 'tie') {
          toast('It\'s a tie!');
        } else {
          toast('Your partner won!');
        }
      })
      .on('broadcast', { event: 'safeword_triggered' }, () => {
        setGameActive(false);
        stop();
      })
      .subscribe();

    // Start beat generator
    beatInterval.current = setInterval(() => {
      setBeatActive(true);
      lastBeatTime.current = Date.now();
      setTimeout(() => setBeatActive(false), 150);
    }, 60000 / bpm);

    return () => {
      supabase.removeChannel(channel);
      if (beatInterval.current) clearInterval(beatInterval.current);
    };
  }, [sessionId, role, vibrate, stop]);

  const hitBeat = useCallback(() => {
    if (!gameActive) return;
    
    const timing = Date.now() - lastBeatTime.current;
    const perfect = timing < 100;
    const hit = timing < 250;

    if (hit) {
      const points = perfect ? 20 : 10;
      const newScore = score + points + (combo * 2);
      setScore(newScore);
      setCombo(prev => prev + 1);
      
      channelRef.current?.send({
        type: 'broadcast',
        event: 'game_action',
        payload: { action: { playerId: role, action: 'beat_hit' }, response: { score: newScore, timing, perfect } }
      });

      // Self feedback
      vibrate(perfect ? 18 : 12);
    } else {
      // Miss
      setCombo(0);
      channelRef.current?.send({
        type: 'broadcast',
        event: 'game_action',
        payload: { action: { playerId: role, action: 'beat_miss' }, response: { timing } }
      });
      vibrate(3);
    }
  }, [gameActive, score, combo, sessionId, role, vibrate]);

  const endGame = () => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'game_ended',
      payload: {
        winner: score > partnerScore ? role : score < partnerScore ? (role === 'A' ? 'B' : 'A') : 'tie'
      }
    });
  };

  if (!gameActive && winner) {
    return (
      <div className="game-container">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{ textAlign: 'center' }}
        >
          <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {winner === role ? '🎉 You Won!' : winner === 'tie' ? '🤝 Tie Game!' : '😊 Nice Try!'}
          </h1>
          <div style={{ display: 'flex', gap: '3rem', justifyContent: 'center', marginBottom: '2rem' }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)' }}>Your Score</p>
              <p style={{ fontSize: '3rem', color: 'var(--primary)' }}>{score}</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)' }}>Partner Score</p>
              <p style={{ fontSize: '3rem', color: 'var(--secondary)' }}>{partnerScore}</p>
            </div>
          </div>
          <button className="btn btn-primary btn-lg" onClick={() => window.location.href = `/session/${sessionId}?role=${role}`}>
            Back to Lobby
          </button>
        </motion.div>
        <SafeWordButton sessionId={sessionId!} />
      </div>
    );
  }

  return (
    <div className="game-container">
      <div style={{ textAlign: 'center' }}>
        <h1>Rhythm Match</h1>
        <p style={{ color: 'var(--text-secondary)' }}>Tap when the circle pulses!</p>
      </div>

      <div className="score-display">
        <div className="score">
          <div className="score-value" style={{ color: 'var(--primary)' }}>{score}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>You</div>
          {combo > 1 && (
            <div style={{ color: 'var(--accent)', fontSize: '1.25rem' }}>
              {combo}x Combo!
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>VS</div>
        <div className="score">
          <div className="score-value" style={{ color: 'var(--secondary)' }}>{partnerScore}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Partner</div>
        </div>
      </div>

      <motion.div
        className="beat-circle"
        animate={beatActive ? { scale: 1.1, backgroundColor: 'var(--primary)' } : { scale: 1, backgroundColor: 'var(--bg-tertiary)' }}
        onMouseDown={hitBeat}
        onTouchStart={hitBeat}
        style={{
          boxShadow: beatActive ? '0 0 40px var(--primary)' : 'none'
        }}
      >
        {beatActive ? 'TAP!' : '...'}
      </motion.div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <button className="btn btn-secondary" onClick={() => vibrate(5)}>Test Low</button>
        <button className="btn btn-secondary" onClick={() => vibrate(15)}>Test High</button>
        <button className="btn btn-danger" onClick={stop}>Stop</button>
        <button className="btn btn-primary" onClick={endGame}>End Game</button>
      </div>

      <SafeWordButton sessionId={sessionId!} />
    </div>
  );
};